/**
 * Verify → repair loop (Blueprint v2 P9). Run diagnostics; on failure, feed back
 * only the failing file + exact error to a generator for a bounded number of
 * retries on the same model, then ESCALATE to a stronger model (Pro-MAX) for a
 * final attempt, then surface a diagnosis. The generator + apply are injected so
 * this orchestration is pure and unit-reasonable; the model call lives in the
 * caller (DeepSeek), keeping this file transport-agnostic.
 */
import type { CheckResult } from './diagnostics.js';
import type { FileMapEntry } from './skills.js';

export interface RepairModel {
  /** Tier id used for the attempt (e.g. fx-pro). */
  readonly tier: string;
  readonly effort: 'off' | 'high' | 'max';
}

export interface RepairContext {
  readonly failures: readonly CheckResult[];
  readonly attempt: number;
  readonly model: RepairModel;
  /** True on the escalated attempt (stronger model). */
  readonly escalated: boolean;
}

export interface RepairOptions {
  /** Generate a fix as a file map, given the focused failure context. */
  generate(ctx: RepairContext): Promise<FileMapEntry[]>;
  /** Apply a generated file map to the workspace. */
  applyFiles(files: readonly FileMapEntry[]): void;
  /** Re-run diagnostics; return the results. */
  verify(): CheckResult[];
  /** Same-model retries before escalation (default 2). */
  maxRetries?: number;
  baseModel: RepairModel;
  /** Stronger model used for the final attempt (Pro-MAX). */
  escalateModel: RepairModel;
  onAttempt?(attempt: number, escalated: boolean, remaining: number): void;
}

export interface RepairOutcome {
  readonly ok: boolean;
  readonly attempts: number;
  readonly escalated: boolean;
  /** Remaining failures when `ok` is false (the diagnosis to surface). */
  readonly finalFailures: CheckResult[];
}

/** Compact failure context for the model: failing check + first errors + files. */
export function summarizeFailures(failures: readonly CheckResult[]): string {
  return failures
    .map((f) => {
      const head = f.output.split('\n').filter((l) => l.trim()).slice(0, 20).join('\n');
      const files = f.failingFiles.length ? `\nfiles: ${f.failingFiles.join(', ')}` : '';
      return `## ${f.name} FAILED (${f.command})${files}\n${head}`;
    })
    .join('\n\n');
}

/**
 * Run the bounded verify→repair→escalate loop. Returns `ok` with the attempt count
 * and escalation flag, or the remaining failures to surface as a diagnosis.
 */
export async function verifyAndRepair(opts: RepairOptions): Promise<RepairOutcome> {
  const maxRetries = opts.maxRetries ?? 2;
  let failures = opts.verify().filter((r) => !r.ok);
  if (failures.length === 0) return { ok: true, attempts: 0, escalated: false, finalFailures: [] };

  // maxRetries same-model attempts, then ONE escalated attempt.
  const totalAttempts = maxRetries + 1;
  let escalatedEver = false;
  for (let attempt = 1; attempt <= totalAttempts; attempt++) {
    const escalated = attempt > maxRetries;
    escalatedEver = escalatedEver || escalated;
    const model = escalated ? opts.escalateModel : opts.baseModel;
    opts.onAttempt?.(attempt, escalated, totalAttempts - attempt);

    const files = await opts.generate({ failures, attempt, model, escalated });
    if (files.length > 0) opts.applyFiles(files);

    failures = opts.verify().filter((r) => !r.ok);
    if (failures.length === 0) {
      return { ok: true, attempts: attempt, escalated: escalatedEver, finalFailures: [] };
    }
  }
  return { ok: false, attempts: totalAttempts, escalated: escalatedEver, finalFailures: failures };
}
